import { Intent, Position, Toaster } from "@blueprintjs/core";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import {
    segmentTrackCreateScenario,
    segmentTrackEditScenario,
    segmentTrackSubmitScenarioRun
} from "in_queue/analytics";
import { useCluster, useStageConfig } from "in_queue/contexts/ClusterContext";
import {
    useAllAvailableProjects,
    useSpeculativeProjectsInCluster,
    useTrackedProject
} from "in_queue/contexts/ProjectDataContext";
import {
    ProjectType,
    SpeculativeProject,
    TrackedProject
} from "in_queue/types/projectType";
import {
    CostAssumption,
    CostAssumptionType,
    MisoPercentageReduction,
    ScenarioMetadata,
    ScenarioStage
} from "in_queue/types/scenarioType";
import {
    getParentProjectId,
    useParentProject
} from "in_queue/utils/scenarioParentProjects";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { costAssumptionTypeToCostAssumption } from "../ScenarioDialog/CostAssumptionsPanel";
import { normalizePriorQueueEris } from "../ScenarioDialog/priorQueueAssumptions";
import { getWholeProjectOnlyProjectIds } from "../ScenarioDialog/wholeProjectOnly";
import {
    defaultProjectSelection,
    ProjectSelection
} from "./defaultProjectSelection";
import {
    getScenarioDialogConfigFromExistingScenario,
    ProjectSizeAssumptionsMetadata,
    useCreateScenario,
    useProjectSizeAssumptionsMetadata,
    useUpdateScenario
} from "./scenarioBuilder.helpers";
import { useCapacityOnlyProjectIds } from "./useCapacityOnlyProjectIds";
import { usePortfolioProjectIds } from "./usePortfolioProjectIds";

export const CreateScenarioToaster = Toaster.create({
    position: Position.TOP
});

const sameReduction = (
    a: MisoPercentageReduction | undefined,
    b: MisoPercentageReduction | undefined
): boolean =>
    (a?.eris ?? 0) === (b?.eris ?? 0) && (a?.nris ?? 0) === (b?.nris ?? 0);

export const supportedCostAssumptions = (
    costAssumptions: CostAssumption,
    supported: CostAssumptionType[]
): CostAssumption =>
    supported.length === 1 && !supported.includes(costAssumptions.type)
        ? costAssumptionTypeToCostAssumption[supported[0]]
        : costAssumptions;

/**
 * True once everything {@link useScenarioBuilderState} seeds from has resolved.
 *
 * Callers must not mount the hook until this is true: it initializes its state
 * once, from the values available on its first render.
 */
export const useIsScenarioBuilderDataReady = (): boolean => {
    const availableProjects = useAllAvailableProjects();
    const speculativeProjects = useSpeculativeProjectsInCluster();
    return availableProjects !== "loading" && speculativeProjects !== "loading";
};

export type ScenarioBuilderAction = "create" | "update";

export type ScenarioSubmitResult =
    | { readonly outcome: "success"; readonly scenarioId: string }
    | { readonly outcome: "no-op" }
    | { readonly outcome: "error" };

interface UseScenarioBuilderStateProps {
    readonly action: ScenarioBuilderAction;
    readonly scenario?: ScenarioMetadata;
    readonly scenarioStage: ScenarioStage;
}

export interface ScenarioBuilderState {
    readonly title: string;
    readonly setTitle: (title: string) => void;
    readonly projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    readonly setProjectSizeAssumptions: (
        assumptions: Record<string, MisoPercentageReduction>
    ) => void;
    readonly includedSpeculativeProjectIds: ReadonlyArray<string>;
    readonly setIncludedSpeculativeProjectIds: (
        ids: ReadonlyArray<string>
    ) => void;
    readonly parentProject: TrackedProject | undefined;
    readonly setParentProject: (project: TrackedProject | undefined) => void;
    readonly costAssumptions: CostAssumption;
    readonly setCostAssumptions: (costAssumptions: CostAssumption) => void;
    readonly selectedUpgradeId: string | null;
    readonly setSelectedUpgradeId: (id: string | null) => void;
    readonly runOnSubmit: boolean;
    readonly setRunOnSubmit: (runOnSubmit: boolean) => void;
    readonly isWarningAcknowledged: boolean;
    readonly setIsWarningAcknowledged: (acknowledged: boolean) => void;
    readonly projectSizeAssumptionsMetadata: ProjectSizeAssumptionsMetadata;
    readonly editedPortfolioProjectIds: ReadonlyArray<string>;
    readonly scenarioStage: ScenarioStage;
    readonly isLoading: boolean;
    readonly disableSubmit: boolean;
    readonly submitTooltip: string | undefined;
    readonly submit: () => Promise<ScenarioSubmitResult>;
}

/**
 * Form state and submit behavior for the scenario builder page.
 *
 * Shows the success and failure toasters itself. Callers own navigation,
 * keyed off the {@link ScenarioSubmitResult}.
 */
export const useScenarioBuilderState = ({
    action,
    scenario,
    scenarioStage
}: UseScenarioBuilderStateProps): ScenarioBuilderState => {
    const createScenario = useCreateScenario();
    const updateScenario = useUpdateScenario();
    const stageConfig = useStageConfig();
    const userData = useUserDataContext();
    const navigate = useNavigate();
    const { projectId } = useParams();
    const cluster = useCluster();
    const project = useTrackedProject({
        projectId,
        phase: cluster.studyPhase
    });

    const availableProjects = useAllAvailableProjects();
    const validProjectIds = useMemo(
        () =>
            availableProjects !== "loading"
                ? availableProjects.map((p) => p.projectId)
                : [],
        [availableProjects]
    );
    const wholeProjectOnlyProjectIds = useMemo(
        () => getWholeProjectOnlyProjectIds(availableProjects),
        [availableProjects]
    );
    const speculativeProjects = useSpeculativeProjectsInCluster();

    const config = getScenarioDialogConfigFromExistingScenario({
        projectId,
        scenario,
        action,
        scenarioStage,
        validProjectIds,
        wholeProjectOnlyProjectIds
    });

    const hasSeed = projectId !== undefined && scenario !== undefined;
    const selectionForConfig = (fromConfig: {
        projectSizeAssumptions: Record<string, MisoPercentageReduction>;
        includedSpeculativeProjectIds: string[];
    }): ProjectSelection =>
        hasSeed
            ? {
                  projectSizeAssumptions: fromConfig.projectSizeAssumptions,
                  includedSpeculativeProjectIds:
                      fromConfig.includedSpeculativeProjectIds
              }
            : defaultProjectSelection(
                  availableProjects === "loading" ? [] : availableProjects
              );

    const [title, setTitle] = useState(config.title);
    const [initialSelection] = useState<ProjectSelection>(() =>
        selectionForConfig(config)
    );
    const [projectSizeAssumptions, setProjectSizeAssumptions] = useState<
        Record<string, MisoPercentageReduction>
    >(initialSelection.projectSizeAssumptions);
    const [parentProject, setParentProject] = useParentProject({
        parentProjectId: config.parentProjectId
    });
    const [costAssumptions, setCostAssumptions] = useState<CostAssumption>(
        supportedCostAssumptions(
            config.costAssumptions,
            stageConfig.supportedCostAssumptionTypes[config.scenarioStage]
        )
    );
    const [includedSpeculativeProjectIds, setIncludedSpeculativeProjectIds] =
        useState<ReadonlyArray<string>>(
            initialSelection.includedSpeculativeProjectIds
        );
    const [selectedUpgradeId, setSelectedUpgradeId] = useState<string | null>(
        config.customUpgradeId
    );
    const [runOnSubmit, setRunOnSubmit] = useState(action !== "update");
    const [isWarningAcknowledged, setIsWarningAcknowledged] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // A scenario saved before the whole-project-only rule can carry a reduction
    // it now forbids. `config` drops it; say so rather than quietly handing the
    // user a different scenario from the one they opened.
    const droppedWholeProjectOnly =
        config.droppedWholeProjectOnlyIds.join(", ");
    useEffect(() => {
        if (droppedWholeProjectOnly) {
            CreateScenarioToaster.show({
                intent: Intent.WARNING,
                message: `${droppedWholeProjectOnly} can only be modelled in service or dropped, so the saved partial reduction was removed.`
            });
        }
    }, [droppedWholeProjectOnly]);

    const projectSizeAssumptionsMetadata = useProjectSizeAssumptionsMetadata({
        projectSizeAssumptions
    });

    const portfolioProjectIds = usePortfolioProjectIds();
    const capacityOnlyProjectIds = useCapacityOnlyProjectIds();
    const baselineAssumptions = useMemo(
        () =>
            normalizePriorQueueEris(
                initialSelection.projectSizeAssumptions,
                capacityOnlyProjectIds
            ).assumptions,
        [initialSelection, capacityOnlyProjectIds]
    );
    const editedPortfolioProjectIds = useMemo(
        () =>
            Array.from(portfolioProjectIds).filter(
                (id) =>
                    initialSelection.includedSpeculativeProjectIds.includes(
                        id
                    ) !== includedSpeculativeProjectIds.includes(id) ||
                    !sameReduction(
                        baselineAssumptions[id],
                        projectSizeAssumptions[id]
                    )
            ),
        [
            portfolioProjectIds,
            initialSelection,
            baselineAssumptions,
            projectSizeAssumptions,
            includedSpeculativeProjectIds
        ]
    );

    const exceedsMwThreshold =
        projectSizeAssumptionsMetadata.percentageMwRemoved >=
        stageConfig.maxMwPercentageRemoved;

    const disableSubmit =
        !title || (exceedsMwThreshold && !isWarningAcknowledged);

    const submit = async (): Promise<ScenarioSubmitResult> => {
        if (
            projectId === undefined ||
            project === undefined ||
            project === "loading" ||
            speculativeProjects === "loading"
        ) {
            return { outcome: "no-op" };
        }

        setIsLoading(true);

        const includedSpeculativeProjects: SpeculativeProject[] =
            speculativeProjects.filter((sp: SpeculativeProject) =>
                includedSpeculativeProjectIds.includes(sp.projectId)
            );
        const parentProjectId = getParentProjectId({
            parentProject,
            projectType: ProjectType.QUEUE_PROJECT
        });
        const parentSpeculativeProjectId = getParentProjectId({
            parentProject,
            projectType: ProjectType.SPECULATIVE_PROJECT
        });

        try {
            if (action === "update") {
                if (!scenario) {
                    // The scenario went away between render and submit.
                    // Falling through here would silently insert a duplicate.
                    setIsLoading(false);
                    CreateScenarioToaster.show({
                        intent: Intent.DANGER,
                        message:
                            "This scenario is no longer available, so it could not be updated."
                    });
                    return { outcome: "error" };
                }
                const updated = await updateScenario({
                    scenarioId: scenario.scenarioId,
                    auth0Id: userData.auth0Id,
                    title,
                    runOnSubmit,
                    projectSizeAssumptions,
                    parentProjectId,
                    parentSpeculativeProjectId,
                    costAssumptions,
                    includedSpeculativeProjects,
                    customUpgradeId: selectedUpgradeId
                });
                setIsLoading(false);
                if (!updated) {
                    CreateScenarioToaster.show({
                        intent: Intent.WARNING,
                        message: "No changes were saved. Please try again."
                    });
                    return { outcome: "no-op" };
                }

                segmentTrackEditScenario({
                    scenarioId: scenario.scenarioId,
                    scenarioTitle: title,
                    numProjectSizeAssumptions: Object.keys(
                        projectSizeAssumptions
                    ).length
                });
                CreateScenarioToaster.show({
                    intent: Intent.SUCCESS,
                    message: `Successfully updated scenario "${updated.title}"`
                });
                return {
                    outcome: "success",
                    scenarioId: scenario.scenarioId
                };
            }

            const created = await createScenario({
                cluster,
                project,
                userData,
                title,
                runOnSubmit,
                projectSizeAssumptions,
                scenarioStage: config.scenarioStage,
                parentProjectId,
                parentSpeculativeProjectId,
                costAssumptions,
                includedSpeculativeProjects,
                customUpgradeId: selectedUpgradeId
            });
            setIsLoading(false);
            if (!created) {
                CreateScenarioToaster.show({
                    intent: Intent.WARNING,
                    message: "The scenario was not created. Please try again."
                });
                return { outcome: "no-op" };
            }

            segmentTrackCreateScenario({
                region: project.cluster.region,
                studyGroup: project.cluster.studyGroup,
                studyCycle: project.cluster.studyCycle,
                studyPhase: project.cluster.studyPhase,
                scenarioStage: config.scenarioStage,
                scenarioTitle: title,
                numProjectSizeAssumptions: Object.keys(projectSizeAssumptions)
                    .length
            });

            // Creating with "Run scenario now" also kicks off the run, so
            // record it as a submit_scenario_run event the way the button does.
            if (runOnSubmit) {
                segmentTrackSubmitScenarioRun({
                    scenarioId: created.scenario_id,
                    source: "scenario_dialog"
                });
            }

            const scenarioHref = `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}/scenario/${created.scenario_id}`;
            CreateScenarioToaster.show({
                intent: Intent.SUCCESS,
                message: `Successfully created new scenario "${created.title}"`,
                action: {
                    text: "Open",
                    onClick: (
                        event: React.MouseEvent<HTMLElement, MouseEvent>
                    ) => {
                        event.preventDefault();
                        navigate(scenarioHref);
                    },
                    href: scenarioHref
                }
            });
            return { outcome: "success", scenarioId: created.scenario_id };
        } catch (error) {
            CreateScenarioToaster.show({
                intent: Intent.DANGER,
                message: `Encountered an error while ${
                    action === "create" ? "creating new" : "updating"
                } scenario. Please try again.`
            });
            setIsLoading(false);
            return { outcome: "error" };
        }
    };

    return {
        title,
        setTitle,
        projectSizeAssumptions,
        setProjectSizeAssumptions,
        includedSpeculativeProjectIds,
        setIncludedSpeculativeProjectIds,
        parentProject,
        setParentProject,
        costAssumptions,
        setCostAssumptions,
        selectedUpgradeId,
        setSelectedUpgradeId,
        runOnSubmit,
        setRunOnSubmit,
        isWarningAcknowledged,
        setIsWarningAcknowledged,
        projectSizeAssumptionsMetadata,
        editedPortfolioProjectIds,
        scenarioStage: config.scenarioStage,
        isLoading,
        disableSubmit,
        submitTooltip: !title ? "Please enter a title" : undefined,
        submit
    };
};
